// BTCNJCC Owner Media Tagger & Google Photos Shared Album Extractor
// Ensures 100% YIR-parity filename prefixing: `(<Owner Name>) <OriginalFilename>`
// Protocol Z-10 / M-10 / S-10 Compliant

/**
 * Clean and sanitize a contributor / ride leader name for safe filename usage.
 * @param {string} name 
 * @returns {string}
 */
export function sanitizeContributorName(name) {
  if (!name || typeof name !== 'string') return 'Contributor';
  let clean = name.trim();
  // Strip control chars and illegal filename characters (<>:"/\|?*)
  clean = clean.replace(/[<>:"/\\|?*\x00-\x1F]/g, '');
  // Strip outer enclosing parentheses if user typed "(Cedric Chow)"
  clean = clean.replace(/^\((.+)\)$/, '$1').trim();
  // Strip trailing dots or spaces which are invalid on Windows/NTFS
  clean = clean.replace(/[.\s]+$/, '').trim();
  return clean || 'Contributor';
}

/**
 * Formats a media filename with YIR-parity parenthesized contributor prefix: `(<CleanName>) <CleanFile>`.
 * Idempotently strips existing `(<Name>)`, `<Name> - `, or `<Name>_` prefixes to avoid duplicate tagging.
 * @param {string} submitterName 
 * @param {string} originalFilename 
 * @returns {string}
 */
export function formatPrefixedMediaFilename(submitterName, originalFilename) {
  if (!originalFilename) return '';
  const cleanName = sanitizeContributorName(submitterName);
  let cleanFile = String(originalFilename).trim();

  // Strip leading path if present (e.g. C:\photos\img.jpg or /photos/img.jpg)
  cleanFile = cleanFile.replace(/^.*[\\/]/, '');

  // 1. Strip any existing parenthesized prefixes idempotently (e.g. "(John) (John) file.jpg" -> "file.jpg")
  cleanFile = cleanFile.replace(/^(\([^)]+\)\s*)+/, '');

  // 2. Strip explicit '<Name> - ' or '<Name>_' prefix matching cleanName
  if (cleanName && cleanName !== 'Contributor') {
    const escaped = cleanName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    cleanFile = cleanFile.replace(new RegExp(`^${escaped}\\s*[-_]\\s*`, 'i'), '');
  }

  // 3. Strip generic '<First Last> - ' or '<First Last>_' prefixes
  cleanFile = cleanFile.replace(/^([A-Za-z]+(?:\s+[A-Za-z]+)+)\s*[-_]\s*/, '');

  // 4. Clean up any residual double spaces
  cleanFile = cleanFile.trim();
  if (!cleanFile) cleanFile = 'media.jpg';

  return `(${cleanName}) ${cleanFile}`;
}

/**
 * Extracts contributor name and base filename from a parenthesized or prefixed filename.
 * @param {string} filename 
 * @returns {{ contributor: string|null, baseFilename: string }}
 */
export function extractContributorFromFilename(filename) {
  if (!filename || typeof filename !== 'string') {
    return { contributor: null, baseFilename: '' };
  }
  const cleanStr = filename.trim().replace(/^.*[\\/]/, '');
  
  // Format 1: (Name) BaseFilename.ext
  const parenMatch = cleanStr.match(/^\(([^)]+)\)\s*(.+)$/);
  if (parenMatch) {
    return {
      contributor: sanitizeContributorName(parenMatch[1]),
      baseFilename: parenMatch[2].trim()
    };
  }

  // Format 2: First Last - BaseFilename.ext or First Last_BaseFilename.ext
  const dashMatch = cleanStr.match(/^([A-Za-z]+(?:\s+[A-Za-z]+)+)\s*[-_]\s*(.+)$/);
  if (dashMatch) {
    return {
      contributor: sanitizeContributorName(dashMatch[1]),
      baseFilename: dashMatch[2].trim()
    };
  }

  return {
    contributor: null,
    baseFilename: cleanStr
  };
}

/**
 * Computes canonical parenthesized filename. If explicitContributorName is provided,
 * formats with it. If not, preserves existing (Name) if present, or defaults to Contributor.
 * @param {string} filename 
 * @param {string} [explicitContributorName] 
 * @returns {string}
 */
export function computeCanonicalParenthesizedFilename(filename, explicitContributorName) {
  if (!filename) return '';
  if (explicitContributorName) {
    return formatPrefixedMediaFilename(explicitContributorName, filename);
  }
  const extracted = extractContributorFromFilename(filename);
  if (extracted.contributor) {
    return formatPrefixedMediaFilename(extracted.contributor, extracted.baseFilename);
  }
  return formatPrefixedMediaFilename('Contributor', filename);
}

/**
 * Parses canonical filename into constituent components.
 * @param {string} filename 
 * @param {string} [fallbackContributor] 
 * @returns {{ contributor: string, baseFilename: string, extension: string, isVideo: boolean, canonicalFilename: string }}
 */
export function parseCanonicalFilenameParts(filename, fallbackContributor) {
  const extracted = extractContributorFromFilename(filename);
  const contributor = extracted.contributor || sanitizeContributorName(fallbackContributor);
  const baseFilename = extracted.baseFilename || 'media.jpg';
  const extMatch = baseFilename.match(/\.([A-Za-z0-9]+)$/);
  const extension = extMatch ? extMatch[1].toLowerCase() : '';
  const isVideo = ['mp4', 'mov', 'webm', 'm4v', 'avi', '3gp', 'mkv'].includes(extension);
  const canonicalFilename = formatPrefixedMediaFilename(contributor, baseFilename);

  return {
    contributor,
    baseFilename,
    extension,
    isVideo,
    canonicalFilename
  };
}

/**
 * Parses original filename from HTTP Content-Disposition header.
 * Handles `filename*=UTF-8''...` and `filename="..."`.
 * @param {string} headerVal 
 * @param {string} [fallbackStem='media'] 
 * @param {boolean} [isVideo=false] 
 * @returns {string}
 */
export function parseContentDispositionFilename(headerVal, fallbackStem = 'media', isVideo = false) {
  const fallbackExt = isVideo ? 'mp4' : 'jpg';
  const defaultFilename = `${fallbackStem || 'media'}.${fallbackExt}`;
  if (!headerVal || typeof headerVal !== 'string') return defaultFilename;

  // RFC 5987 / RFC 6266 filename*=UTF-8''...
  const starMatch = headerVal.match(/filename\*=(?:UTF-8|utf-8)''([^;\s]+)/i);
  if (starMatch && starMatch[1]) {
    try {
      const decoded = decodeURIComponent(starMatch[1].trim());
      const clean = decoded.replace(/^.*[\\/]/, '').replace(/[<>:"/\\|?*\x00-\x1F]/g, '');
      if (clean) return clean;
    } catch (_) {}
  }

  // Quoted filename="..."
  const quotedMatch = headerVal.match(/filename="([^"]+)"/i);
  if (quotedMatch && quotedMatch[1]) {
    const clean = quotedMatch[1].trim().replace(/^.*[\\/]/, '').replace(/[<>:"/\\|?*\x00-\x1F]/g, '');
    if (clean) return clean;
  }

  // Bare filename=...
  const bareMatch = headerVal.match(/filename=([^;\s]+)/i);
  if (bareMatch && bareMatch[1]) {
    const clean = bareMatch[1].trim().replace(/['"]/g, '').replace(/^.*[\\/]/, '').replace(/[<>:"/\\|?*\x00-\x1F]/g, '');
    if (clean) return clean;
  }

  return defaultFilename;
}

/**
 * Robust JSON bracket extractor for AF_initDataCallback payloads.
 * @param {string} html 
 * @param {string} key 
 * @returns {any}
 */
function extractInitDataCallback(html, key = 'ds:1') {
  const keyPattern = new RegExp(`key:\\s*['"]${key}['"]`);
  const keyIdx = html.search(keyPattern);
  if (keyIdx === -1) return null;

  const dataIdx = html.indexOf('data:', keyIdx);
  if (dataIdx === -1) return null;

  const bracketStart = html.indexOf('[', dataIdx);
  if (bracketStart === -1) return null;

  // Extract balanced JSON bracket
  let depth = 0;
  let inString = false;
  let escapeNext = false;
  let endIdx = -1;

  for (let i = bracketStart; i < html.length; i++) {
    const char = html[i];
    if (escapeNext) {
      escapeNext = false;
      continue;
    }
    if (char === '\\' && inString) {
      escapeNext = true;
      continue;
    }
    if (char === '"' || char === "'") {
      if (!inString) {
        inString = char;
      } else if (inString === char) {
        inString = false;
      }
      continue;
    }
    if (inString) continue;

    if (char === '[') {
      depth++;
    } else if (char === ']') {
      depth--;
      if (depth === 0) {
        endIdx = i + 1;
        break;
      }
    }
  }

  if (endIdx !== -1) {
    const jsonStr = html.slice(bracketStart, endIdx);
    try {
      return JSON.parse(jsonStr);
    } catch (_) {}
  }

  return null;
}

/**
 * Parses a Google Photos Shared Album HTML payload.
 * Extracts album title, actor roster (mapping contributor names to items), and media items.
 * @param {string} html 
 * @param {string} [sourceUrl=''] 
 * @returns {{ albumTitle: string, sourceUrl: string, photoCount: number, contributors: Array<{ actorId: string, name: string, count: number, avatarUrl?: string }>, items: Array<{ mediaId: string, baseUrl: string, width: number, height: number, timestampMs: number, actorId: string, contributorName: string, isVideo: boolean, downloadUrl: string, thumbUrl: string, suggestedFilename: string }> }}
 */
export function parseGooglePhotosSharedAlbumHtml(html, sourceUrl = '') {
  if (!html || typeof html !== 'string') {
    return {
      albumTitle: 'Google Photos Album',
      sourceUrl,
      photoCount: 0,
      contributors: [],
      items: []
    };
  }

  // Extract Album Title
  let albumTitle = 'Google Photos Album';
  const ogTitleMatch = html.match(/<meta\s+(?:property=["']og:title["']\s+content=["']([^"']+)["']|content=["']([^"']+)["']\s+property=["']og:title["'])/i);
  if (ogTitleMatch && (ogTitleMatch[1] || ogTitleMatch[2])) {
    albumTitle = (ogTitleMatch[1] || ogTitleMatch[2]).trim();
  } else {
    const docTitleMatch = html.match(/<title>([^<]+)<\/title>/i);
    if (docTitleMatch && docTitleMatch[1]) {
      albumTitle = docTitleMatch[1].replace(/\s*-\s*Google Photos\s*$/i, '').trim();
    }
  }

  // Extract ds:1 payload
  const ds1Data = extractInitDataCallback(html, 'ds:1');
  const actorsMap = new Map();

  // Parse Actor Roster from ds1Data[3] if available
  if (ds1Data && Array.isArray(ds1Data[3])) {
    if ((!albumTitle || albumTitle === 'Google Photos Album') && typeof ds1Data[3][1] === 'string' && ds1Data[3][1].trim()) {
      albumTitle = ds1Data[3][1].trim();
    }
    const actorList = ds1Data[3].find(
      x => Array.isArray(x) && x.length > 0 && Array.isArray(x[0]) && Array.isArray(x[0][0])
    ) || ds1Data[3];
    for (const entry of actorList) {
      if (!Array.isArray(entry)) continue;
      // Actor entry structure: [[actorId, gaiaId], null, null, [fullName, 1, null, firstName], ...]
      const actorId = Array.isArray(entry[0]) ? String(entry[0][0] || '') : String(entry[0] || '');
      const nameDetails = Array.isArray(entry[3]) ? entry[3] : [];
      let fullName = typeof nameDetails[0] === 'string' ? nameDetails[0] : '';
      let firstName = typeof nameDetails[3] === 'string' ? nameDetails[3] : '';
      let avatarUrl = typeof entry[1] === 'string' ? entry[1] : (typeof entry[2] === 'string' ? entry[2] : '');

      if (!fullName) {
        // Fallback search for string in entry
        for (const item of entry) {
          if (Array.isArray(item) && typeof item[0] === 'string' && item[0].length > 1) {
            fullName = item[0];
            break;
          }
        }
      }

      if (actorId && fullName) {
        actorsMap.set(actorId, {
          actorId,
          name: sanitizeContributorName(fullName),
          firstName: firstName || fullName.split(' ')[0],
          avatarUrl
        });
      }
    }
  }

  const items = [];
  const contributorCounts = new Map();

  // Parse Media Items from ds1Data[1]
  if (ds1Data && Array.isArray(ds1Data[1])) {
    for (const it of ds1Data[1]) {
      if (!Array.isArray(it)) continue;
      const mediaId = String(it[0] || '');
      const baseUrl = it[1] && typeof it[1][0] === 'string' ? it[1][0] : '';
      if (!baseUrl) continue;

      const width = (it[1] && Number(it[1][1])) || 0;
      const height = (it[1] && Number(it[1][2])) || 0;
      const timestampMs = Number(it[2]) || 0;

      // Actor ID is typically at it[6][0]
      const actorId = it[6] && Array.isArray(it[6]) ? String(it[6][0] || '') : (typeof it[6] === 'string' ? it[6] : '');
      const contributorInfo = actorsMap.get(actorId);
      const contributorName = contributorInfo?.name || 'Contributor';

      // Video check in Google Photos metadata: it[9]['76647426'] or it[15]['76647426']
      let isVideo = false;
      if (it[9] && typeof it[9] === 'object' && ('76647426' in it[9])) {
        isVideo = true;
      } else if (it[15] && typeof it[15] === 'object' && ('76647426' in it[15])) {
        isVideo = true;
      }

      const downloadUrl = isVideo ? `${baseUrl}=dv` : `${baseUrl}=d`;
      const thumbUrl = `${baseUrl}=w400-h300-p-k`;
      const rawStem = mediaId ? mediaId.slice(0, 12) : 'media';
      const ext = isVideo ? 'mp4' : 'jpg';
      const suggestedFilename = formatPrefixedMediaFilename(contributorName, `${rawStem}.${ext}`);

      // Tally contributor count
      contributorCounts.set(contributorName, (contributorCounts.get(contributorName) || 0) + 1);

      items.push({
        mediaId,
        baseUrl,
        width,
        height,
        timestampMs,
        actorId,
        contributorName,
        isVideo,
        downloadUrl,
        thumbUrl,
        suggestedFilename
      });
    }
  }

  // Fallback: If ds:1 parsing yielded 0 items, regex scan LH3 URLs
  if (items.length === 0) {
    const lh3Matches = html.match(/\["([A-Za-z0-9_\-]+)",\["(https:\/\/lh3\.googleusercontent\.com\/pw\/[A-Za-z0-9_\-]+)"/g);
    if (lh3Matches && lh3Matches.length > 0) {
      const seen = new Set();
      for (const m of lh3Matches) {
        const subMatch = m.match(/\["([A-Za-z0-9_\-]+)",\["(https:\/\/lh3\.googleusercontent\.com\/pw\/[A-Za-z0-9_\-]+)"/);
        if (subMatch) {
          const mediaId = subMatch[1];
          const baseUrl = subMatch[2];
          if (seen.has(baseUrl)) continue;
          seen.add(baseUrl);

          const suggestedFilename = formatPrefixedMediaFilename('Contributor', `${mediaId.slice(0, 12)}.jpg`);
          contributorCounts.set('Contributor', (contributorCounts.get('Contributor') || 0) + 1);

          items.push({
            mediaId,
            baseUrl,
            width: 0,
            height: 0,
            timestampMs: 0,
            actorId: '',
            contributorName: 'Contributor',
            isVideo: false,
            downloadUrl: `${baseUrl}=d`,
            thumbUrl: `${baseUrl}=w400-h300-p-k`,
            suggestedFilename
          });
        }
      }
    }
  }

  // Build sorted contributors array
  const contributors = [];
  for (const [name, count] of contributorCounts.entries()) {
    // Look up avatarUrl if available
    let avatarUrl = '';
    for (const actor of actorsMap.values()) {
      if (actor.name === name && actor.avatarUrl) {
        avatarUrl = actor.avatarUrl;
        break;
      }
    }
    contributors.push({
      name,
      count,
      avatarUrl
    });
  }
  contributors.sort((a, b) => b.count - a.count);

  return {
    albumTitle,
    sourceUrl,
    photoCount: items.length,
    contributors,
    items
  };
}

/**
 * Wraps a browser File/Blob into a new owner-prefixed File instance.
 * @param {File|Blob} file 
 * @param {string} contributorName 
 * @returns {{ file: File, originalFilename: string, prefixedFilename: string, contributorName: string, size: number, isVideo: boolean }}
 */
export function createOwnerTaggedFile(file, contributorName) {
  if (!file) throw new Error('createOwnerTaggedFile requires a valid file');
  const originalFilename = file.name || 'media.jpg';
  const cleanContributor = sanitizeContributorName(contributorName);
  const prefixedFilename = formatPrefixedMediaFilename(cleanContributor, originalFilename);
  const isVideo = /^video\//i.test(file.type || '') || /\.(mp4|mov|webm|m4v|avi|3gp)$/i.test(originalFilename);

  // Compatible File constructor (browser or Node mock)
  let taggedFile;
  const FileConstructor = typeof File !== 'undefined' ? File : class MockFile {
    constructor(parts, name, opts) {
      this.parts = parts;
      this.name = name;
      this.type = opts?.type || '';
      this.lastModified = opts?.lastModified || Date.now();
      this.size = parts && parts[0] && typeof parts[0].size === 'number' ? parts[0].size : 0;
    }
  };

  try {
    taggedFile = new FileConstructor([file], prefixedFilename, {
      type: file.type || (isVideo ? 'video/mp4' : 'image/jpeg'),
      lastModified: file.lastModified || Date.now()
    });
  } catch (_) {
    taggedFile = file;
  }

  return {
    file: taggedFile,
    originalFilename,
    prefixedFilename,
    contributorName: cleanContributor,
    size: file.size || 0,
    isVideo
  };
}

// ============================================================================
// PURE-JS PKZIP ARCHIVE BUILDER (STORE METHOD 0, UTF-8 EFS, IEEE 802.3 CRC-32)
// Zero External Dependencies - Works in Browser and Node.js
// ============================================================================

let crc32Table = null;
function getCrc32Table() {
  if (!crc32Table) {
    const table = new Uint32Array(256);
    for (let i = 0; i < 256; i++) {
      let c = i;
      for (let k = 0; k < 8; k++) {
        c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
      }
      table[i] = c >>> 0;
    }
    crc32Table = table;
  }
  return crc32Table;
}

/**
 * Computes standard IEEE 802.3 CRC-32 checksum for a Uint8Array.
 * @param {Uint8Array} data 
 * @returns {number} 32-bit unsigned integer
 */
export function computeCrc32(data) {
  const table = getCrc32Table();
  let crc = 0xFFFFFFFF;
  for (let i = 0; i < data.length; i++) {
    crc = table[(crc ^ data[i]) & 0xFF] ^ (crc >>> 8);
  }
  return (crc ^ 0xFFFFFFFF) >>> 0;
}

/**
 * Deduplicates a filename within a target ZIP archive to prevent overwrite collisions.
 * Preserves forward-slash ('/') directory segments (e.g. "Bstar/(Will Cheng) IMG_01.jpg")
 * per PKZIP APPNOTE 4.4.17.1 so category subfolders are created inside the ZIP archive.
 * e.g. "(Cedric Chow) IMG_01.jpg" -> "(Cedric Chow) IMG_01 (2).jpg"
 * @param {string} rawName 
 * @param {Set<string>} existingNames 
 * @returns {string}
 */
export function deduplicateZipFilename(rawName, existingNames) {
  const normalized = String(rawName || 'media').replace(/\\/g, '/').trim();
  const segments = normalized
    .split('/')
    .map(seg => seg.replace(/[<>:"|?*\x00-\x1F]/g, '').trim().replace(/[. ]+$/, ''))
    .filter(Boolean);
  let name = segments.length > 0 ? segments.join('/') : 'media';
  const lower = name.toLowerCase();
  if (!existingNames.has(lower)) {
    existingNames.add(lower);
    return name;
  }

  const lastSlash = name.lastIndexOf('/');
  const lastDot = name.lastIndexOf('.');
  const hasExt = lastDot > lastSlash + 1;
  const stem = hasExt ? name.slice(0, lastDot) : name;
  const ext = hasExt ? name.slice(lastDot) : '';

  let counter = 2;
  while (true) {
    const candidate = `${stem} (${counter})${ext}`;
    if (!existingNames.has(candidate.toLowerCase())) {
      existingNames.add(candidate.toLowerCase());
      return candidate;
    }
    counter++;
  }
}

/**
 * Packages multiple files into a standard PKZIP archive Blob in pure JS.
 * Uses STORE compression (method = 0) with UTF-8 flag (0x0800), IEEE 802.3 CRC-32,
 * Local File Headers, Central Directory Headers, and End of Central Directory Record.
 *
 * @param {Array<{ filename?: string, name?: string, data: Uint8Array | ArrayBuffer | Blob | string }>} entries
 * @param {object} [options={}]
 * @param {boolean} [options.forceZip64=false] Force writing ZIP64 End of Central Directory & Locator records
 * @returns {Promise<{ blob: Blob, bytes: Uint8Array | null, fileCount: number, totalSize: number, isZip64: boolean }>}
 */
export async function createZipArchiveBlob(entries = [], options = {}) {
  if (!Array.isArray(entries)) {
    throw new TypeError('createZipArchiveBlob expects an array of file entries');
  }

  const encoder = new TextEncoder();
  const existingNames = new Set();
  const validEntries = [];

  for (const entry of entries) {
    if (!entry) continue;
    const rawName = entry.filename || entry.name || 'media';
    const filename = deduplicateZipFilename(rawName, existingNames);
    const filenameBytes = encoder.encode(filename);

    let dataBytes;
    if (entry.data instanceof Uint8Array) {
      dataBytes = entry.data;
    } else if (entry.data instanceof ArrayBuffer) {
      dataBytes = new Uint8Array(entry.data);
    } else if (typeof Blob !== 'undefined' && entry.data instanceof Blob) {
      dataBytes = new Uint8Array(await entry.data.arrayBuffer());
    } else if (typeof entry.data === 'string') {
      dataBytes = encoder.encode(entry.data);
    } else if (entry.data && typeof entry.data.arrayBuffer === 'function') {
      dataBytes = new Uint8Array(await entry.data.arrayBuffer());
    } else if (entry.data && typeof entry.data.buffer === 'object') {
      dataBytes = new Uint8Array(entry.data.buffer, entry.data.byteOffset || 0, entry.data.byteLength || entry.data.length || 0);
    } else {
      dataBytes = new Uint8Array(0);
    }

    const crc = computeCrc32(dataBytes);
    validEntries.push({
      filename,
      filenameBytes,
      dataBytes,
      crc,
      size: dataBytes.length
    });
  }

  // Pre-calculate exact byte layout and offsets
  let localDataSize = 0;
  let cdSize = 0;
  for (const item of validEntries) {
    item.localOffset = localDataSize;
    item.needsZip64Offset = item.localOffset >= 0xFFFFFFFF;
    item.cdExtraLen = item.needsZip64Offset ? 12 : 0; // 0x0001 tag (2) + size 8 (2) + 64-bit offset (8)
    localDataSize += 30 + item.filenameBytes.length + item.size;
    cdSize += 46 + item.filenameBytes.length + item.cdExtraLen;
  }

  const cdOffset = localDataSize;
  const useZip64 = Boolean(
    options.forceZip64 ||
    localDataSize >= 0xFFFFFFFF ||
    cdSize >= 0xFFFFFFFF ||
    validEntries.length >= 0xFFFF
  );

  const zip64Overhead = useZip64 ? (56 + 20) : 0; // 56-byte ZIP64 EOCD + 20-byte ZIP64 Locator
  const eocdSize = 22;
  const totalArchiveSize = localDataSize + cdSize + zip64Overhead + eocdSize;

  const now = new Date();
  const year = Math.max(1980, now.getFullYear());
  const dosDate = (((year - 1980) << 9) | ((now.getMonth() + 1) << 5) | now.getDate()) & 0xFFFF;
  const dosTime = ((now.getHours() << 11) | (now.getMinutes() << 5) | Math.floor(now.getSeconds() / 2)) & 0xFFFF;

  // Zero-Copy Chunked Blob Assembly for multi-GB scalability
  const blobParts = [];

  // 1. Build Local File Header chunks paired directly with raw dataBytes (zero payload copy)
  for (const item of validEntries) {
    const localHeader = new Uint8Array(30 + item.filenameBytes.length);
    const lhView = new DataView(localHeader.buffer);
    lhView.setUint32(0, 0x04034b50, true);          // Local File Header signature
    lhView.setUint16(4, 20, true);                  // Version needed to extract (2.0)
    lhView.setUint16(6, 0x0800, true);              // General purpose bit flag (bit 11 = UTF-8)
    lhView.setUint16(8, 0, true);                   // Compression method (0 = STORE)
    lhView.setUint16(10, dosTime, true);            // File mod time
    lhView.setUint16(12, dosDate, true);            // File mod date
    lhView.setUint32(14, item.crc, true);           // CRC-32
    lhView.setUint32(18, Math.min(item.size, 0xFFFFFFFF), true); // Compressed size
    lhView.setUint32(22, Math.min(item.size, 0xFFFFFFFF), true); // Uncompressed size
    lhView.setUint16(26, item.filenameBytes.length, true);       // Filename length
    lhView.setUint16(28, 0, true);                  // Extra field length
    localHeader.set(item.filenameBytes, 30);

    blobParts.push(localHeader);
    blobParts.push(item.dataBytes);
  }

  // 2. Write Central Directory Headers buffer
  const cdBuffer = new Uint8Array(cdSize);
  const cdView = new DataView(cdBuffer.buffer);
  let cdPos = 0;

  for (const item of validEntries) {
    const versionNeeded = item.needsZip64Offset ? 45 : 20;
    cdView.setUint32(cdPos, 0x02014b50, true);      // Central Directory signature
    cdView.setUint16(cdPos + 4, versionNeeded, true); // Version made by
    cdView.setUint16(cdPos + 6, versionNeeded, true); // Version needed to extract
    cdView.setUint16(cdPos + 8, 0x0800, true);      // UTF-8 flag
    cdView.setUint16(cdPos + 10, 0, true);          // Compression method (STORE)
    cdView.setUint16(cdPos + 12, dosTime, true);    // File mod time
    cdView.setUint16(cdPos + 14, dosDate, true);    // File mod date
    cdView.setUint32(cdPos + 16, item.crc, true);   // CRC-32
    cdView.setUint32(cdPos + 20, Math.min(item.size, 0xFFFFFFFF), true); // Compressed size
    cdView.setUint32(cdPos + 24, Math.min(item.size, 0xFFFFFFFF), true); // Uncompressed size
    cdView.setUint16(cdPos + 28, item.filenameBytes.length, true);       // Filename length
    cdView.setUint16(cdPos + 30, item.cdExtraLen, true);                 // Extra field length
    cdView.setUint16(cdPos + 32, 0, true);          // File comment length
    cdView.setUint16(cdPos + 34, 0, true);          // Disk number start
    cdView.setUint16(cdPos + 36, 0, true);          // Internal file attributes
    cdView.setUint32(cdPos + 38, 0, true);          // External file attributes
    cdView.setUint32(cdPos + 42, item.needsZip64Offset ? 0xFFFFFFFF : item.localOffset, true);
    cdPos += 46;

    cdBuffer.set(item.filenameBytes, cdPos);
    cdPos += item.filenameBytes.length;

    if (item.needsZip64Offset) {
      cdView.setUint16(cdPos, 0x0001, true);        // ZIP64 Extended Info Extra Field Tag
      cdView.setUint16(cdPos + 2, 8, true);         // Data size (8 bytes for 64-bit localOffset)
      cdView.setBigUint64(cdPos + 4, BigInt(item.localOffset), true);
      cdPos += 12;
    }
  }
  blobParts.push(cdBuffer);

  // 3. Write ZIP64 End of Central Directory Record (56B) + Locator (20B) when enabled
  if (useZip64) {
    const zip64Buffer = new Uint8Array(76);
    const z64View = new DataView(zip64Buffer.buffer);
    const zip64EocdOffset = cdOffset + cdSize;

    // ZIP64 End of Central Directory Record (56 bytes)
    z64View.setUint32(0, 0x06064b50, true);         // ZIP64 EOCD signature
    z64View.setBigUint64(4, 44n, true);             // Size of remaining ZIP64 EOCD record (56 - 12 = 44)
    z64View.setUint16(12, 45, true);                // Version made by (4.5)
    z64View.setUint16(14, 45, true);                // Version needed to extract (4.5)
    z64View.setUint32(16, 0, true);                 // Number of this disk
    z64View.setUint32(20, 0, true);                 // Disk with start of CD
    z64View.setBigUint64(24, BigInt(validEntries.length), true); // Entries on this disk
    z64View.setBigUint64(32, BigInt(validEntries.length), true); // Total entries
    z64View.setBigUint64(40, BigInt(cdSize), true);              // Size of Central Directory
    z64View.setBigUint64(48, BigInt(cdOffset), true);            // Offset of Central Directory

    // ZIP64 End of Central Directory Locator (20 bytes at offset 56)
    z64View.setUint32(56, 0x07064b50, true);        // ZIP64 EOCD Locator signature
    z64View.setUint32(60, 0, true);                 // Disk with start of ZIP64 EOCD
    z64View.setBigUint64(64, BigInt(zip64EocdOffset), true); // Relative offset of ZIP64 EOCD
    z64View.setUint32(72, 1, true);                 // Total number of disks

    blobParts.push(zip64Buffer);
  }

  // 4. Write Standard End of Central Directory Record (22 bytes)
  const eocdBuffer = new Uint8Array(eocdSize);
  const eocdView = new DataView(eocdBuffer.buffer);
  eocdView.setUint32(0, 0x06054b50, true);          // EOCD signature
  eocdView.setUint16(4, 0, true);                   // Disk number
  eocdView.setUint16(6, 0, true);                   // Disk with start of central directory
  eocdView.setUint16(8, useZip64 ? 0xFFFF : Math.min(validEntries.length, 0xFFFF), true);
  eocdView.setUint16(10, useZip64 ? 0xFFFF : Math.min(validEntries.length, 0xFFFF), true);
  eocdView.setUint32(12, useZip64 ? 0xFFFFFFFF : Math.min(cdSize, 0xFFFFFFFF), true);
  eocdView.setUint32(16, useZip64 ? 0xFFFFFFFF : Math.min(cdOffset, 0xFFFFFFFF), true);
  eocdView.setUint16(20, 0, true);                  // Comment length
  blobParts.push(eocdBuffer);

  // Materialize contiguous Uint8Array only for small archives (<= 64 MB) to preserve unit-test byte inspection
  // without hitting V8 contiguous typed-array allocation limits on multi-GB archives.
  let mergedBytes = null;
  if (totalArchiveSize <= 64 * 1024 * 1024) {
    mergedBytes = new Uint8Array(totalArchiveSize);
    let offset = 0;
    for (const part of blobParts) {
      mergedBytes.set(part, offset);
      offset += part.byteLength;
    }
  }

  let blob;
  if (typeof Blob !== 'undefined') {
    blob = new Blob(blobParts, { type: 'application/zip' });
  } else {
    blob = {
      type: 'application/zip',
      size: totalArchiveSize,
      arrayBuffer: async () => (mergedBytes ? mergedBytes.buffer.slice(mergedBytes.byteOffset, mergedBytes.byteOffset + mergedBytes.byteLength) : new ArrayBuffer(0))
    };
  }

  return {
    blob,
    bytes: mergedBytes,
    fileCount: validEntries.length,
    totalSize: totalArchiveSize,
    isZip64: useZip64
  };
}

/**
 * Resolves the target category subfolder for a ride in the annual Media Center archive.
 * Follows the standard BTCNJCC taxonomy:
 * Official BTCNJ rides -> 'Astar', 'A', 'Bstar', 'Bplus', 'B', 'Cstar', 'Cplus', 'C', 'Dstar', 'D', 'Gravel-MTB', or 'Uncategorized-BTCNJ'.
 * Non-BTCNJ rides (e.g. Gavin's Farewell Guestbook, custom rides) -> 'Non-BTCNJ'.
 *
 * @param {object} ride
 * @returns {string}
 */
export function resolveRideCategoryFolder(ride) {
  if (!ride) return 'Non-BTCNJ';

  const rawRideId = ride.rideId ? String(ride.rideId).trim() : '';
  const hasValidBtcnjId = Boolean(rawRideId && !/^(custom|other|ride-custom)-/i.test(rawRideId));
  const isBtcnj = Boolean(hasValidBtcnjId || ride.isBtcnjRide);

  if (!isBtcnj) {
    return 'Non-BTCNJ';
  }

  const rawCat = ride.category != null ? String(ride.category).trim() : '';
  if (!rawCat) {
    return 'Uncategorized-BTCNJ';
  }

  // Strip trailing "ride" or "rides"
  const cat = rawCat.replace(/\s*rides?$/i, '').trim();
  if (!cat) {
    return 'Uncategorized-BTCNJ';
  }

  if (/^(a\/a\*|a\*|astar)$/i.test(cat)) return 'Astar';
  if (/^a$/i.test(cat)) return 'A';
  if (/^(b\/b\+|b\*|bstar)$/i.test(cat)) return 'Bstar';
  if (/^(b\+|bplus)$/i.test(cat)) return 'Bplus';
  if (/^b$/i.test(cat)) return 'B';
  if (/^(c\/c\+|c\*|cstar)$/i.test(cat)) return 'Cstar';
  if (/^(c\+|cplus)$/i.test(cat)) return 'Cplus';
  if (/^c$/i.test(cat)) return 'C';
  if (/^(d\*|dstar)$/i.test(cat)) return 'Dstar';
  if (/^d$/i.test(cat)) return 'D';
  if (/^(gravel\/mtb|gravel|mtb)$/i.test(cat)) return 'Gravel-MTB';

  // Custom or unmapped non-empty category
  let sanitized = cat
    .replace(/\*/g, 'star')
    .replace(/\+/g, 'plus')
    .replace(/[/\\]+/g, '-')
    .replace(/[<>:"/\\|?*\x00-\x1F]/g, '')
    .trim()
    .replace(/[. ]+$/, '');

  return sanitized || 'Uncategorized-BTCNJ';
}

/**
 * Resolves an OS-safe, collision-free relative ZIP entry path.
 * Enforces Windows MAX_PATH safety (< 180 chars), guards DOS reserved device names,
 * and performs 2-tier collision disambiguation:
 *  - Tier 1: _YYYYMMDD cross-ride date tagging
 *  - Tier 2: (2), (3) same-ride / residual counter disambiguation
 *
 * @param {string} categoryFolder e.g. "Bstar"
 * @param {string} prefixedFilename e.g. "(Colleen Cornell) IMG_4821.jpg"
 * @param {object} [options={}] e.g. { dateTag: '20260815', dateIso: '2026-08-15', rideId: '123' }
 * @param {Set<string>|Map<string, any>|object} [collisionRegistry=new Set()]
 * @returns {string} e.g. "Bstar/(Colleen Cornell) IMG_4821.jpg"
 */
export function resolveOsSafeZipEntryPath(categoryFolder, prefixedFilename, options = {}, collisionRegistry = new Set()) {
  let cleanFolder = String(categoryFolder || 'Non-BTCNJ')
    .replace(/[<>:"/\\|?*\x00-\x1F]/g, '')
    .trim()
    .replace(/[. ]+$/, '') || 'Non-BTCNJ';

  let cleanFile = String(prefixedFilename || 'media')
    .replace(/^[/\\]+/, '')
    .replace(/[<>:"/\\|?*\x00-\x1F]/g, '')
    .trim()
    .replace(/[. ]+$/, '') || 'media';

  const lastDot = cleanFile.lastIndexOf('.');
  let stem = lastDot > 0 ? cleanFile.slice(0, lastDot) : cleanFile;
  const ext = lastDot > 0 ? cleanFile.slice(lastDot) : '';

  // Guard Windows reserved DOS device names (CON, PRN, AUX, NUL, COM1-9, LPT1-9)
  if (/^(CON|PRN|AUX|NUL|COM[1-9]|LPT[1-9])$/i.test(stem)) {
    stem = `_${stem}`;
  }

  // Windows MAX_PATH length clamping: stem <= 120 chars ensures total path < 180 chars
  if (stem.length > 120) {
    stem = stem.slice(0, 120).trim();
  }

  // Normalize dateTag (e.g. "20260815" from "2026-08-15")
  const rawDate = options.dateTag || options.dateIso || '';
  const dateTag = rawDate ? String(rawDate).replace(/[^A-Za-z0-9]/g, '').slice(0, 12) : '';

  const hasKey = (key) => {
    if (!collisionRegistry) return false;
    if (collisionRegistry instanceof Set || (typeof collisionRegistry.has === 'function' && !(collisionRegistry instanceof Map))) {
      return collisionRegistry.has(key);
    }
    if (collisionRegistry instanceof Map) {
      return collisionRegistry.has(key);
    }
    return Boolean(collisionRegistry[key]);
  };

  const addKey = (key) => {
    if (!collisionRegistry) return;
    if (collisionRegistry instanceof Set) {
      collisionRegistry.add(key);
    } else if (collisionRegistry instanceof Map) {
      collisionRegistry.set(key, true);
    } else if (typeof collisionRegistry === 'object') {
      collisionRegistry[key] = true;
    }
  };

  // Candidate 0: Pristine path
  const cand0 = `${cleanFolder}/${stem}${ext}`;
  if (!hasKey(cand0.toLowerCase())) {
    addKey(cand0.toLowerCase());
    return cand0;
  }

  // Tier 1 (Cross-Ride Clash with dateTag)
  if (dateTag && !stem.endsWith(`_${dateTag}`)) {
    const cand1 = `${cleanFolder}/${stem}_${dateTag}${ext}`;
    if (!hasKey(cand1.toLowerCase())) {
      addKey(cand1.toLowerCase());
      return cand1;
    }
  }

  // Tier 2 (Same-Ride / Residual Clash with counter)
  const baseStem = (dateTag && !stem.endsWith(`_${dateTag}`)) ? `${stem}_${dateTag}` : stem;
  let counter = 2;
  while (true) {
    const candN = `${cleanFolder}/${baseStem} (${counter})${ext}`;
    if (!hasKey(candN.toLowerCase())) {
      addKey(candN.toLowerCase());
      return candN;
    }
    counter++;
  }
}

/**
 * Groups Media Center rides by year and category folders for the Annual Bulk Downloader.
 *
 * @param {Array<object>} rides
 * @param {string|number} [targetYear]
 * @returns {{ year: string, availableYears: string[], totalAlbums: number, totalEstimatedPhotos: number, categories: Array<{ folderName: string, isNonBtcnj: boolean, albumCount: number, photoCount: number, albums: object[] }> }}
 */
export function groupMediaCenterRidesByYearAndCategory(rides = [], targetYear = null) {
  const albumRides = (Array.isArray(rides) ? rides : []).filter(r => {
    if (!r) return false;
    if (r.deleted || r.isDeleted) return false;
    return Boolean(r.photoAlbumUrl && String(r.photoAlbumUrl).trim());
  });

  function getRideYear(r) {
    if (r.dateIso) {
      const m = String(r.dateIso).match(/^(\d{4})/);
      if (m) return m[1];
    }
    if (r.dateStr) {
      const m = String(r.dateStr).match(/\b(20\d{2})\b/);
      if (m) return m[1];
    }
    if (r.date) {
      const d = new Date(r.date);
      if (!isNaN(d.getTime())) return String(d.getFullYear());
    }
    return String(new Date().getFullYear());
  }

  const allYears = Array.from(new Set(albumRides.map(getRideYear))).sort((a, b) => b.localeCompare(a));
  const resolvedYear = targetYear ? String(targetYear) : (allYears[0] || String(new Date().getFullYear()));

  const matchingRides = albumRides.filter(r => getRideYear(r) === resolvedYear);

  const categoryMap = new Map();
  for (const r of matchingRides) {
    const folderName = resolveRideCategoryFolder(r);
    if (!categoryMap.has(folderName)) {
      categoryMap.set(folderName, []);
    }
    categoryMap.get(folderName).push(r);
  }

  const CANONICAL_ORDER = [
    'Astar', 'A', 'Bstar', 'Bplus', 'B', 'Cstar', 'Cplus', 'C', 'Dstar', 'D', 'Gravel-MTB', 'Uncategorized-BTCNJ', 'Non-BTCNJ'
  ];

  const sortedFolderNames = Array.from(categoryMap.keys()).sort((a, b) => {
    const idxA = CANONICAL_ORDER.indexOf(a);
    const idxB = CANONICAL_ORDER.indexOf(b);
    if (idxA !== -1 && idxB !== -1) return idxA - idxB;
    if (idxA !== -1) return -1;
    if (idxB !== -1) return 1;
    return a.localeCompare(b);
  });

  let totalAlbums = 0;
  let totalEstimatedPhotos = 0;
  const categories = sortedFolderNames.map(folderName => {
    const ridesInCat = categoryMap.get(folderName);
    const albumCount = ridesInCat.length;
    const photoCount = ridesInCat.reduce((sum, r) => {
      const cnt = Number(r.photosCount || r.photoCount || (Array.isArray(r.photos) ? r.photos.length : 0) || (Array.isArray(r.mediaItems) ? r.mediaItems.length : 0)) || 1;
      return sum + cnt;
    }, 0);
    totalAlbums += albumCount;
    totalEstimatedPhotos += photoCount;
    return {
      folderName,
      isNonBtcnj: folderName === 'Non-BTCNJ',
      albumCount,
      photoCount,
      albums: ridesInCat
    };
  });

  return {
    year: String(resolvedYear),
    availableYears: allYears,
    totalAlbums,
    totalEstimatedPhotos,
    categories
  };
}

/**
 * Creates multi-volume PKZIP archives partitioned so no single volume exceeds maxVolumeBytes.
 * Supports 4 GB Windows Standard default, 8 GB ZIP64, and Unlimited Single-ZIP (maxVolumeBytes = 0 or Infinity).
 *
 * @param {Array<{ filename?: string, name?: string, data: Uint8Array|ArrayBuffer|Blob|string, size?: number }>} entries
 * @param {object} [options={}]
 * @param {number} [options.maxVolumeBytes=4294967296] Default 4 GB (0 or Infinity = Unlimited Single ZIP)
 * @param {string} [options.baseZipName='BTCNJCC_Media_Center (Tagged)']
 * @returns {Promise<Array<{ partNumber: number, totalParts: number, zipFilename: string, blob: Blob, bytes: Uint8Array|null, fileCount: number, totalSize: number, isZip64: boolean }>>}
 */
export async function createMultiVolumeZipArchives(entries = [], options = {}) {
  const rawMax = options.maxVolumeBytes;
  const maxVolumeBytes = (rawMax === 0 || rawMax === Infinity || rawMax === Number.MAX_SAFE_INTEGER)
    ? Number.MAX_SAFE_INTEGER
    : (Number(rawMax) > 0 ? Number(rawMax) : (4 * 1024 * 1024 * 1024));
  const baseZipName = String(options.baseZipName || 'BTCNJCC_Media_Center (Tagged)').replace(/\.zip$/i, '').trim();

  function getEntryByteSize(entry) {
    if (!entry) return 0;
    let dataLen = 0;
    if (entry.data instanceof Uint8Array || entry.data instanceof ArrayBuffer) {
      dataLen = entry.data.byteLength || entry.data.length || 0;
    } else if (typeof Blob !== 'undefined' && entry.data instanceof Blob) {
      dataLen = entry.data.size || 0;
    } else if (typeof entry.data === 'string') {
      dataLen = new TextEncoder().encode(entry.data).length;
    } else if (entry.size) {
      dataLen = Number(entry.size) || 0;
    }
    const nameLen = entry.filename ? entry.filename.length : 30;
    return dataLen + nameLen + 100; // local + cd header overhead
  }

  const batches = [];
  let currentBatch = [];
  let currentBatchBytes = 0;

  for (const entry of entries) {
    const itemSize = getEntryByteSize(entry);
    if (currentBatch.length > 0 && (currentBatchBytes + itemSize > maxVolumeBytes)) {
      batches.push(currentBatch);
      currentBatch = [entry];
      currentBatchBytes = itemSize;
    } else {
      currentBatch.push(entry);
      currentBatchBytes += itemSize;
    }
  }
  if (currentBatch.length > 0 || batches.length === 0) {
    batches.push(currentBatch);
  }

  const totalParts = batches.length;
  const results = [];
  for (let partIdx = 0; partIdx < totalParts; partIdx++) {
    const batch = batches[partIdx];
    const zipResult = await createZipArchiveBlob(batch);
    const zipFilename = totalParts === 1
      ? `${baseZipName}.zip`
      : `${baseZipName} - Part ${partIdx + 1} of ${totalParts}.zip`;

    results.push({
      partNumber: partIdx + 1,
      totalParts,
      zipFilename,
      blob: zipResult.blob,
      bytes: zipResult.bytes,
      fileCount: zipResult.fileCount,
      totalSize: zipResult.totalSize,
      isZip64: zipResult.isZip64
    });
  }
  return results;
}

/**
 * Formats remaining seconds into a fixed-width, zero-padded ETA string that never bounces
 * when transitioning between single-digit and double-digit minutes or seconds.
 *
 * - Warmup / invalid / negative / non-finite -> 'ETA --m --s' (11 chars)
 * - < 3600s -> 'ETA MMm SSs' (11 chars, e.g. 'ETA 00m 07s', 'ETA 09m 59s', 'ETA 12m 04s')
 * - >= 3600s -> 'ETA HHh MMm SSs' (15 chars, e.g. 'ETA 01h 04m 09s')
 *
 * @param {number|null|undefined} remainingSeconds
 * @returns {string}
 */
export function formatStableDurationEta(remainingSeconds) {
  if (
    remainingSeconds === null ||
    remainingSeconds === undefined ||
    typeof remainingSeconds !== 'number' ||
    !Number.isFinite(remainingSeconds) ||
    remainingSeconds < 0
  ) {
    return 'ETA --m --s';
  }

  const clamped = Math.max(0, Math.ceil(remainingSeconds));
  const hours = Math.floor(clamped / 3600);
  const minutes = Math.floor((clamped % 3600) / 60);
  const seconds = clamped % 60;

  const mm = String(minutes).padStart(2, '0');
  const ss = String(seconds).padStart(2, '0');

  if (hours > 0) {
    const hh = String(hours).padStart(2, '0');
    return `ETA ${hh}h ${mm}m ${ss}s`;
  }
  return `ETA ${mm}m ${ss}s`;
}

/**
 * Computes monotonically non-decreasing progress percentage and EMA-smoothed,
 * cadence-locked ETA telemetry for the Annual Media Center ZIP downloader.
 *
 * @param {object} state
 * @param {number} state.completedItems
 * @param {number} state.expectedTotalItems
 * @param {number} state.elapsedMs
 * @param {number} [state.previousPercent=0]
 * @param {number} [state.previousEmaRate=0]
 * @param {string} [state.previousEtaText='ETA --m --s']
 * @param {number} [state.lastEtaUpdateMs=0]
 * @param {boolean} [state.isComplete=false]
 * @returns {{ percent: number, percentText: string, emaRate: number, remainingSeconds: number|null, etaText: string, lastEtaUpdateMs: number }}
 */
export function computeAnnualDownloadTelemetry(state = {}) {
  const completedItems = Math.max(0, Number(state.completedItems) || 0);
  const expectedTotalItems = Math.max(completedItems, Number(state.expectedTotalItems) || 0);
  const elapsedMs = Math.max(0, Number(state.elapsedMs) || 0);
  const previousPercent = Math.max(0, Number(state.previousPercent) || 0);
  const previousEmaRate = Math.max(0, Number(state.previousEmaRate) || 0);
  const previousEtaText = typeof state.previousEtaText === 'string' && state.previousEtaText
    ? state.previousEtaText
    : 'ETA --m --s';
  const lastEtaUpdateMs = Math.max(0, Number(state.lastEtaUpdateMs) || 0);
  const isComplete = Boolean(state.isComplete);

  if (isComplete) {
    return {
      percent: 100,
      percentText: '100.0%',
      emaRate: previousEmaRate,
      remainingSeconds: 0,
      etaText: formatStableDurationEta(0),
      lastEtaUpdateMs: elapsedMs
    };
  }

  let rawPct = expectedTotalItems > 0 ? (completedItems / expectedTotalItems) * 100 : 0;
  rawPct = Math.min(99.5, Math.max(0, rawPct));
  const percent = Math.max(previousPercent, rawPct);
  const percentText = `${percent.toFixed(1)}%`;

  // Warmup threshold: need at least 2 completed files and >= 800ms elapsed to estimate velocity
  if (completedItems < 2 || elapsedMs < 800 || expectedTotalItems <= 0) {
    return {
      percent,
      percentText,
      emaRate: previousEmaRate,
      remainingSeconds: null,
      etaText: 'ETA --m --s',
      lastEtaUpdateMs
    };
  }

  const elapsedSec = elapsedMs / 1000;
  const instRate = completedItems / elapsedSec;
  const emaRate = previousEmaRate > 0
    ? (0.25 * instRate + 0.75 * previousEmaRate)
    : instRate;

  const remainingItems = Math.max(0, expectedTotalItems - completedItems);
  const remainingSeconds = emaRate > 0 ? (remainingItems / emaRate) : null;

  // Throttle visual ETA text updates to >= 800ms cadence (unless transitioning out of warmup '--m --s')
  const isFirstValidEstimate = previousEtaText === 'ETA --m --s';
  const shouldRefreshEtaText = isFirstValidEstimate || (elapsedMs - lastEtaUpdateMs >= 800) || remainingItems === 0;
  const etaText = shouldRefreshEtaText
    ? formatStableDurationEta(remainingSeconds)
    : previousEtaText;

  return {
    percent,
    percentText,
    emaRate,
    remainingSeconds,
    etaText,
    lastEtaUpdateMs: shouldRefreshEtaText ? elapsedMs : lastEtaUpdateMs
  };
}
